/**
 * Slice 1C frontend — komponenty Alpine.js dla stanów magazynowych i ETA.
 *
 * 2026-06-11 refactor pre-fire:
 *  - stockEtaHandler czyta progi z data-attrib przy init (Szczecin/Lanberg + 2 daty ETA).
 *  - Zmiana qty = lokalna kalkulacja bez fetcha REST. Zero zwłoki przy klikaniu +/-.
 *  - REST /breakdown tylko przy zmianie wariantu lub po 30s stale-cache.
 *  - Zero popupów na initial load i zmianę wariantu.
 *  - Inline contextual notification (pod ETA) gdy user świadomie przekroczy próg Szczecin.
 *
 * Eksportuje:
 *   - warehouseStock(warehouse, postId)  — stan per magazyn (Szczecin / Mosina)
 *   - stockEtaHandler(postId)            — dynamiczny badge ETA na karcie produktu (pre-fire)
 *   - cutoffBadgeHandler(carrier)        — hero/footer dynamiczny cutoff
 *
 * REST endpoint: /wp-json/kablowo/v1/breakdown + /cutoff-badge (cache 30s)
 */

const REST_BASE = '/wp-json/kablowo/v1';
const STALE_CACHE_MS = 30_000;
const INLINE_NOTICE_DURATION_MS = 5_000;
const INLINE_NOTICE_THROTTLE_MS = 5_000;

// Polska deklinacja: szpula / szpule / szpul (lub karton / kartony / kartonów)
function pluralPl(n, forms) {
    n = Math.abs(n);
    if (n === 1) return forms[0];
    const last2 = n % 100;
    const last = n % 10;
    if (last2 >= 12 && last2 <= 14) return forms[2];
    if (last >= 2 && last <= 4) return forms[1];
    return forms[2];
}

function packageForms(unit) {
    return unit === 'mb'
        ? ['szpula', 'szpule', 'szpul']
        : ['opakowanie', 'opakowania', 'opakowań'];
}

function formatInt(n) {
    return new Intl.NumberFormat('pl-PL').format(n);
}

// ──────────────────────────────────────────────────────────────────
// warehouseStock — info-box "X szt dostępne" przy każdym magazynie
// ──────────────────────────────────────────────────────────────────
// v1.6.2: warehouseStock przestaje SAM fetchować REST — nasłuchuje na event 'stock-eta:updated'
// z breakdown z stockEtaHandler (master fetcher).
export const warehouseStock = (warehouse, postId) => ({
    warehouse: warehouse,
    loading: true,
    hasStock: false,
    displayText: 'Sprawdzanie...',

    init() {
        window.addEventListener('stock-eta:updated', (e) => {
            if (!e.detail || !e.detail.breakdown) return;
            this.applyBreakdown(e.detail.breakdown);
        });
    },

    applyBreakdown(bd) {
        const stock = this.warehouse === 'szczecin'
            ? (bd.szczecin || 0)
            : (bd.lanberg || 0);
        const unit = bd.unit || 'szt';

        this.hasStock = stock > 0;
        this.loading = false;

        if (stock === 0) {
            this.displayText = 'Brak na tym magazynie';
        } else if (unit === 'mb' && bd.qty_per_package && bd.qty_per_package > 0) {
            // PART IX Krok 3a v3 (2026-06-15): dla kabli zawsze pokazuj sztuki + mb w nawiasie.
            // Klient wpisuje qty w sztukach (= szpulach) - konsystentnie pokazuj szt jako primary.
            const szpule = Math.floor(stock / bd.qty_per_package);
            const pkgWord = pluralPl(szpule, packageForms(unit));
            this.displayText = `${formatInt(szpule)} ${pkgWord} (${formatInt(stock)} ${unit})`;
        } else {
            this.displayText = `${formatInt(stock)} ${unit} dostępne`;
        }
    }
});

// ──────────────────────────────────────────────────────────────────
// stockEtaHandler — badge ETA na karcie produktu (pre-fire)
// ──────────────────────────────────────────────────────────────────
export const stockEtaHandler = (postId) => ({
    postId: postId,
    selectedVariation: postId,
    qty: 1,
    cableLength: 0,
    loading: false,
    abortController: null,
    fetchDebounceTimer: null,
    lastFetchAt: 0,

    // Pre-fire data (czytane z data-attrib przy init, aktualizowane przy zmianie wariantu).
    prefire: {
        szczeciQty: 0,
        supplierQty: 0,
        totalQty: 0,
        etaShortPl: '',
        etaLongPl: '',
        unit: 'szt',
        stockDisplay: '', // B5 (2026-06-11): "Dostępne: X szt/mb" - SSR initial
    },

    // Throttle dla floating notification (Hubert 2026-06-11: powiadomienie idzie do prawego
    // dolnego rogu jak "Produkt dodany do koszyka").
    lastInlineNoticeAt: 0,

    ui: {
        class: 'bg-gray-50 text-gray-700 border-gray-200',
        message: '',
        allowsAddToCart: true,
        maxQuantity: 9999,
        unit: 'szt',
    },

    init() {
        // 1) Czytaj pre-fire dane z DOM (SSR data-attrib z template-parts/product-stock-eta.php).
        const el = this.$el;
        if (el) {
            this.prefire = {
                szczeciQty:   parseInt(el.dataset.szczecinQty, 10) || 0,
                supplierQty:  parseInt(el.dataset.supplierQty, 10) || 0,
                totalQty:     parseInt(el.dataset.totalQty, 10) || 0,
                etaShortPl:   el.dataset.etaShortPl || '',
                etaLongPl:    el.dataset.etaLongPl  || '',
                unit:         el.dataset.unit || 'szt',
                stockDisplay: el.dataset.stockDisplay || '',
            };
            // PART IX Krok 3a v2 (2026-06-15): inicjuj cableLength z data-attrib zeby
            // applyPrefireToUi liczyl maxQuantity w SZTUKACH od initial load (nie tylko po
            // product-qty:changed event). Bez tego maxQuantity = totalQty w mb dla kabli.
            this.cableLength = parseInt(el.dataset.cableLength, 10) || 0;
            // 2) Initial UI z pre-fire bez fetcha REST.
            this.applyPrefireToUi(this.qty);
        }

        // 3) Eventy: zmiana wariantu → fetch (nowe progi); zmiana qty → lokalna kalkulacja.
        window.addEventListener('variant:selected', (e) => {
            if (e.detail && e.detail.variationId) {
                this.selectedVariation = parseInt(e.detail.variationId, 10);
                // Zero popup na zmianę wariantu (Hubert 2026-06-11).
                this.scheduleFetch(50);
            }
        });

        window.addEventListener('cable:lengthChanged', (e) => {
            if (e.detail && typeof e.detail.length !== 'undefined') {
                this.cableLength = parseFloat(e.detail.length) || 0;
                // Cable length change może zmienić effective qty znacząco; aktualizujemy lokalnie.
                this.applyPrefireToUi(this.effectiveQty());
                // Plus fetch po debounce (np. krzepkie zmiany długości mogą wymagać świeżych danych).
                this.scheduleFetch(300);
            }
        });

        // Zmiana qty z addToCartHandler — pre-fire lokalna kalkulacja + ewentualny inline notice.
        window.addEventListener('product-qty:changed', (e) => {
            if (!e.detail) return;
            const prevQty = this.qty;
            if (typeof e.detail.qty === 'number' && e.detail.qty > 0) {
                this.qty = e.detail.qty;
            }
            if (typeof e.detail.cableLength === 'number') {
                this.cableLength = e.detail.cableLength;
            }
            const eff = this.effectiveQty();
            this.applyPrefireToUi(eff);

            // Inline notification TYLKO gdy świadoma zmiana qty (userInitiated) przekroczyła próg Szczecin.
            // Reguła Huberta 2026-06-11: zero popupu na init / zmianę wariantu / zmianę produktu.
            if (e.detail.userInitiated === true && this.prefire.szczeciQty > 0 && this.prefire.supplierQty > 0) {
                const wasUnderThreshold = prevQty <= this.prefire.szczeciQty;
                const isOverThreshold   = eff > this.prefire.szczeciQty && eff <= this.prefire.totalQty;
                if (wasUnderThreshold && isOverThreshold) {
                    this.showInlineNotice();
                }
            }

            // Stale cache: gdy minęło >30s od ostatniego fetcha, odśwież w tle.
            if (Date.now() - this.lastFetchAt > STALE_CACHE_MS) {
                this.scheduleFetch(500);
            }
        });
    },

    /**
     * Stosuje pre-fire dane do this.ui na podstawie qty bez fetcha.
     * Logika identyczna jak backend kablowo_resolve_routing (R1/R2/R3).
     */
    applyPrefireToUi(qty) {
        const { szczeciQty, supplierQty, totalQty, etaShortPl, etaLongPl, unit } = this.prefire;

        // PART IX Krok 3a fix W-7 (2026-06-15): maxQuantity w SZTUKACH (= quantity input units),
        // nie mb. Dla kabla: totalQty (mb) / cableLength (mb per szpula) = liczba szpul.
        // Dla non-cable: totalQty (sztuki) zostaje. Bez tego addToCartHandler clampuje quantity
        // do tysiecy szpul (totalQty w mb), klient moze kliknac + bez limitu.
        const maxOrderQty = this.cableLength > 0
            ? Math.floor(totalQty / this.cableLength)
            : totalQty;

        // Shortage: qty > total
        if (qty > totalQty && totalQty >= 0) {
            this.ui = {
                ...this.ui,
                class: 'border bg-red-50 text-red-700 border-red-200',
                message: 'Brak takiej ilości na stanie',
                allowsAddToCart: false,
                maxQuantity: maxOrderQty,
                unit,
            };
        }
        // Cały Szczecin (qty <= szczecin): ETA short
        else if (qty <= szczeciQty && szczeciQty > 0) {
            this.ui = {
                ...this.ui,
                class: 'border bg-green-50 text-green-700 border-green-200',
                message: etaShortPl ? `Wysyłka ${etaShortPl}` : '',
                allowsAddToCart: true,
                maxQuantity: maxOrderQty,
                unit,
            };
        }
        // Powyżej Szczecin (lub cały Lanberg gdy szczecin=0): ETA long
        else if (qty <= totalQty) {
            this.ui = {
                ...this.ui,
                class: 'border bg-green-50 text-green-700 border-green-200',
                message: etaLongPl ? `Wysyłka ${etaLongPl}` : '',
                allowsAddToCart: true,
                maxQuantity: maxOrderQty,
                unit,
            };
        }

        // Wyemituj event - productHandler nasłuchuje (stockStatus) + warehouseStock + add-to-cart.
        // PART IX Krok 3a v3 (2026-06-15): dla kabli pokazuj sztuki (= szpule) + mb w nawiasie.
        // Klient widzi "Dostępne: 430 szpul (131 325 mb)" zamiast "Dostępne: 131 325 mb".
        // Sztuki match z quantity input + maxQuantity dispatch (po Krok 3a v2).
        let subtitle = '';
        if (totalQty > 0) {
            if (this.cableLength > 0) {
                const szpule = Math.floor(totalQty / this.cableLength);
                subtitle = `Dostępne: ${formatInt(szpule)} szt (${formatInt(totalQty)} ${unit})`;
            } else {
                subtitle = `Dostępne: ${formatInt(totalQty)} ${unit}`;
            }
        }
        // PART XII W-8 (2026-06-16): dorzuc availableToAdd dla preemptive guard w addToCart.
        // Backend validator (class-stock-resolver.php linia 516) i tak liczy istniejacy cart,
        // ale tu frontent blokuje oczywisty overflow (qty > stock max) BEZ 5-10s spin.
        window.dispatchEvent(new CustomEvent('stock-eta:updated', {
            detail: {
                ...this.ui,
                subtitle: subtitle,
                availableToAdd: maxOrderQty,
                breakdown: { szczecin: szczeciQty, lanberg: supplierQty, total: totalQty, unit, qty_per_package: this.prefire.qty_per_package }
            }
        }));
    },

    /**
     * 2026-06-11 v3: floating toast w prawym dolnym rogu - jak "Produkt dodany do koszyka".
     * Tekst = kopia statycznego MIX. Throttle 5s.
     */
    showInlineNotice() {
        const now = Date.now();
        if (now - this.lastInlineNoticeAt < INLINE_NOTICE_THROTTLE_MS) return;
        this.lastInlineNoticeAt = now;
        const { szczeciQty, etaShortPl, etaLongPl, unit } = this.prefire;
        window.dispatchEvent(new CustomEvent('show-notification', {
            detail: {
                message: `Powyżej ${szczeciQty} ${unit} wysyłka ${etaLongPl}. Do ${szczeciQty} ${unit} mamy w Szczecinie i wyślemy ${etaShortPl}.`,
                type: 'warning',
                duration: INLINE_NOTICE_DURATION_MS,
            }
        }));
    },

    scheduleFetch(delay) {
        clearTimeout(this.fetchDebounceTimer);
        this.fetchDebounceTimer = setTimeout(() => this.fetchBreakdown(), delay);
    },

    effectiveQty() {
        return this.cableLength > 0
            ? this.cableLength * (this.qty || 1)
            : (this.qty || 1);
    },

    async fetchBreakdown() {
        if (!this.selectedVariation) return;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.loading = true;
        try {
            const r = await fetch(
                `${REST_BASE}/breakdown?post_id=${this.selectedVariation}&qty=${this.effectiveQty()}`,
                { signal: this.abortController.signal }
            );
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            this.lastFetchAt = Date.now();

            // Zaktualizuj pre-fire z odpowiedzi REST (nowy wariant = nowe progi i daty).
            if (d.prefire) {
                this.prefire = {
                    szczeciQty:  parseInt(d.prefire.szczecin_qty, 10) || 0,
                    supplierQty: parseInt(d.prefire.supplier_qty, 10) || 0,
                    totalQty:    parseInt(d.prefire.total_qty, 10) || 0,
                    etaShortPl:  d.prefire.eta_short_pl || '',
                    etaLongPl:   d.prefire.eta_long_pl  || '',
                    unit:        d.prefire.unit || 'szt',
                };
            } else if (d.ui) {
                this.ui = d.ui;
            }

            // PART IX Krok 3a v3 (2026-06-15): applyPrefireToUi generuje subtitle z konwersją
            // mb → sztuki dla kabli (lokalna logika). NIE używamy d.ui.subtitle z REST (= mb tylko).
            // Plus przekazujemy breakdown w dispatch dla warehouseStock per-magazyn (osobny event).
            this.applyPrefireToUi(this.effectiveQty());

            // Breakdown dla warehouseStock — osobny event bo subtitle juz dispatched powyzej.
            if (d.breakdown) {
                window.dispatchEvent(new CustomEvent('stock-eta:updated', {
                    detail: {
                        ...this.ui,
                        breakdown: d.breakdown,
                    }
                }));
            }
        } catch (e) {
            if (e.name === 'AbortError') return;
            console.error('stockEtaHandler fetch failed', e);
        } finally {
            this.loading = false;
        }
    }
});

// ──────────────────────────────────────────────────────────────────
// cutoffBadgeHandler — hero/footer dynamiczny cutoff badge
// ──────────────────────────────────────────────────────────────────
export const cutoffBadgeHandler = (carrier = 'inpost', initialMessage = '') => ({
    carrier: carrier,
    message: initialMessage,
    class: 'gray',
    minutesLeft: 0,
    refreshTimer: null,

    init() {
        // PHP wyrenderował świeży komunikat SSR (cache 60s), więc pomijamy
        // pierwszy fetch — pierwszy refresh dopiero po 60s.
        this.refreshTimer = setInterval(() => this.refresh(), 60_000);
    },

    destroy() {
        if (this.refreshTimer) clearInterval(this.refreshTimer);
    },

    async refresh() {
        try {
            const r = await fetch(`${REST_BASE}/cutoff-badge?carrier=${this.carrier}`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            this.message = d.message || this.message;
            this.class = d.class || 'gray';
            this.minutesLeft = d.minutes_left || 0;
        } catch (e) {
            // Silent fail — zostaw poprzedni komunikat
        }
    }
});
